import * as signalR from '@microsoft/signalr';
import { NgZone, Injectable } from '@angular/core';
import { Subject, timer ,  Observable, Subscription } from 'rxjs';
import { HttpHeaders } from '@angular/common/http';
import { DeviceCheckinCompleted, DeviceCheckinResultCode } from './models/device-checkin-completed';
import { DeviceCheckinFailure } from './models/device-checkin-failure';
import { CheckinStateBroadcastService, DeviceCheckinStatusMessage, DeviceStatusRequest, DeviceIdToCheckinStateMap } from './broadcasters/checkin.broadcaster';
import { GlobalEvents } from '../../core/global-event-constants';
import { UntypedTopicUpdateModel } from './models/topic-update';
import { TopicDescriptorModel } from './models/topic-descriptor';
import { AuthenticationService, DeviceService, FeatureToggleService, GlobalEventBusService, IRequestOptions, LicenceInfoService, XDeviceService, XSightHttpService } from '@nx-fe/services';
import { SignalRCoreService } from './signalr-core.service';

export enum ConnectionState {
    Connecting = 1,
    Connected = 2,
    Reconnecting = 3,
    Disconnected = 4
}

interface DeviceCheckinConfig {
    MaximumCheckInTimeout: number;
}

@Injectable({
    providedIn: 'root'
})
export class SignalRService {
    private _subscribeTopicSubscription: Subscription;
    private _unsubscribeTopicSubscription: Subscription;
    private readonly onDeviceCheckinStatusChanged: CheckinStateBroadcastService;
    private _configuration: DeviceCheckinConfig = { MaximumCheckInTimeout:  30000 };
    connectionInitiated: boolean;

    constructor(
        private http: XSightHttpService,
        private zone: NgZone,
        private authenticationService: AuthenticationService,
        private deviceService: XDeviceService,
        private saLicenceInfoService: LicenceInfoService,
        private globalEventBus: GlobalEventBusService,
        private ftService: FeatureToggleService,
        private signalRCoreService: SignalRCoreService // with this dependency here we could init SignalRCoreService with the legacy service
    ) {
        const isSignalRCoreEnabled = ftService.isEnabled('EnableAspNetCore') && ftService.isEnabled('EnableAspNetCore_SignalRService');
        if(isSignalRCoreEnabled === true) {
            console.log('SignalRCore enabled. Skipping legacy SignalR initialization');
            return;
        }

        // Set up our broadcastservice
        this.connectionState$ = this.connectionStateSubject.asObservable();
        this.error$ = this.errorSubject.asObservable();
        this.onDeviceCheckinStatusChanged = new CheckinStateBroadcastService();

        this._subscribeTopicSubscription = this.globalEventBus.register(GlobalEvents.subscribeTopic, (result: TopicDescriptorModel) => {
            this.hub.invoke('SubscribeTopic', result);
        });

        this._unsubscribeTopicSubscription = this.globalEventBus.register(GlobalEvents.unsubscribeTopic, (result: TopicDescriptorModel) => {
            this.hub.invoke('UnsubscribeTopic', result);
        });

        this.tryInitiateConnect();
    }

    /**
     * connectionState$ provides the current state of the underlying
     * connection as an observable stream.
     */
    public connectionState$: Observable<ConnectionState>;

    /**
     * error$ provides a stream of any error messages that occur on the
     * SignalR connection
     */
    public error$: Observable<string>;
    deviceCheckinCompleted$: Observable<DeviceCheckinCompleted>;
    deviceCheckinError$: Observable<DeviceCheckinFailure>;
    deviceCheckinInProgress$: Observable<any>;

    private checkinStatus: DeviceIdToCheckinStateMap = {};

    private connectionStateSubject = new Subject<ConnectionState>();
    private errorSubject = new Subject<any>();
    private hub: any;
    private hubName = 'liveDataHub';
    private connection: any;


    tryInitiateConnect = () => {
        this.saLicenceInfoService.getLicenceInfoPromise()?.then(() => {
            if (!this.connectionInitiated) {
                this.tryInitiateConnectInternal();
            }
        });
    };

    /** This method requires licenseDetails to be available.
     * Should be called only from tryInitiateConnect()
     */
    tryInitiateConnectInternal = () => {
        if (!this.isLicenseActive()) {
            console.warn('SignalR service is disabled due to the inactive license');
            return;
        }

        this.connectionInitiated = true;

        this.onDeviceCheckinStatusChanged.onMessage = (msg: DeviceCheckinStatusMessage) => {
            switch (msg.request) {
                case DeviceStatusRequest.WatchDevice:
                this.startCheckinInProgress(msg.deviceId, this._configuration.MaximumCheckInTimeout);
                    break;
                case DeviceStatusRequest.StopWatchingDevice:
                    this.stopCheckinInProgress(msg.deviceId);
                    break;
                case DeviceStatusRequest.Refresh:
                    break;
            }
        };

        // If there's no feature toggle, Service does nothing
        this.setupHub();
        this.ConfigureMessaging();
        this.startConnection();
    };

    private isLicenseActive = (): boolean => {
        const result = this.saLicenceInfoService && this.saLicenceInfoService.isLicenseActive();
        return result;
    };

    private startCheckinInProgress = (deviceId: string, timeout: number) => {
        this.checkinStatus[deviceId] = new Date();
        this.deviceService.requestDevicesCheckin(deviceId, true, true, timeout).subscribe(
            () => {
                const timeoutDate = new Date();
                timer(this._configuration.MaximumCheckInTimeout).subscribe(() => {
                    // only issue timeout if it is still being watched, and a new checkin hasn't started
                    if (this.checkinStatus[deviceId] && this.checkinStatus[deviceId] < timeoutDate) {
                        this.broadcastTimeout(deviceId);
                        this.stopCheckinInProgress(deviceId);
                    }
                });
            }, error => {
                delete this.checkinStatus[deviceId];
                this.broadcastError(deviceId, { deviceId, errorCode: 999, errorDescription: 'checkin failed' });
            });
        this.broadcastStatus();
    };

    private broadcastStatus = () => {
        this.onDeviceCheckinStatusChanged.postMessage({
            request: DeviceStatusRequest.Status,
            deviceCheckinStatus: this.checkinStatus
        });
    };

    private broadcastComplete = (data: DeviceCheckinCompleted) => {
        this.onDeviceCheckinStatusChanged.postMessage({
            request: DeviceStatusRequest.Completed,
            deviceCheckinStatus: this.checkinStatus,
            data
        });
    };

    private broadcastTimeout = (deviceId: string) => {
        this.onDeviceCheckinStatusChanged.postMessage({
            request: DeviceStatusRequest.Timeout,
            deviceCheckinStatus: this.checkinStatus,
            data: { deviceId, checkinTime: new Date(), result: DeviceCheckinResultCode.CheckinTimeout }
        });
    };

    private broadcastError = (deviceId: string, data: DeviceCheckinFailure) => {
        this.onDeviceCheckinStatusChanged.postMessage({
            deviceId,
            request: DeviceStatusRequest.Error,
            deviceCheckinStatus: this.checkinStatus,
            data
        });
    };

    private stopCheckinInProgress = (deviceId: string) => {
        delete this.checkinStatus[deviceId];
        this.broadcastStatus();
    };

    private setupHub() {
        // create hub connection
        this.connection = hubConnection('/sotixsight/signalr') as any;
        this.connection.logging = true;
        // create new proxy as name already given in top
        this.hub = this.connection.createHubProxy(this.hubName);

        // Define handlers for the connection state events
        this.connection.stateChanged(this.onConnectionStateChanged.bind(this));

        // Define handlers for any errors
        this.connection.error((error: any) => {
            this.zone.run(() => {
                // Push the error on our subject
                this.errorSubject.next(error);
            });
        });
    }

    private onConnectionStateChanged(state: any) {
        let newState = ConnectionState.Connecting;

        switch (state.newState) {
            case signalR.HubConnectionState.Connecting:
                newState = ConnectionState.Connecting;
                break;
            case signalR.HubConnectionState.Connected:
                newState = ConnectionState.Connected;
                break;
            case signalR.HubConnectionState.Reconnecting:
                newState = ConnectionState.Reconnecting;
                break;
            case signalR.HubConnectionState.Disconnected:
                newState = ConnectionState.Disconnected;
                setTimeout(() => { this.startConnection(); }, 10000);
                break;
        }

        // Add ngZone to make UI apply the async data
        this.zone.run(() => {
            // Push the new state on our subject
            this.connectionStateSubject.next(newState);
        });


    }

    private startConnection(): void {
        // Setting STP
        this.getOneTimePasswordResource().then(stp => {
            this.connection.qs = { stp };

            this.connection.start()
                .done((data: any) => {
                    console.log('SignalR connection established');
                    this.globalEventBus.broadcast(GlobalEvents.signalrInitialized, true);
                }).fail((error: any) => {

                    console.error('SignalR connection could not be established');
                });
        });

    }

    private getOneTimePasswordResource = (): Promise<string> => {

        const deferred = new Promise<string>((resolve, reject) => {


            this.authenticationService.isAuthenticated().then(async () => {
                try {
                  const stp = await this.http.get<string>('signalr/stp').toPromise();
                  resolve(stp);
                } catch (error) {
                  reject(error);
                }
            }).catch(error => {
                reject(error);
            });
        });

        return deferred;
    };

    private ConfigureMessaging(): void {
        this.hub.on('onReauthenticateRequest', (data: string) => {
            this.reauthenticate();
        });

        this.hub.on('onDeviceCheckinCompleted', (data: DeviceCheckinCompleted) => {
            if (this.checkinStatus[data.deviceId]) {
                this.stopCheckinInProgress(data.deviceId);
                this.broadcastComplete(data);
            }
        });

        this.hub.on('onDeviceCheckinFailed', (data: DeviceCheckinFailure) => {
            if (this.checkinStatus[data.deviceId]) {
                this.stopCheckinInProgress(data.deviceId);
            }
        });

        this.hub.on('onTopicUpdate', (data: UntypedTopicUpdateModel) => {
            this.globalEventBus.broadcast(data.topicName, data.data);
        });
    }

    private reauthenticate() {
        this.authenticationService.isAuthenticated();
    }

}
function hubConnection(arg0: string): signalR.HubConnection {
    const x  = new signalR.HubConnectionBuilder().withUrl(arg0);
    const hub = x.build();
    return hub;
}

